import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LEAD_STATUSES, LEAD_STATUS_LABELS } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type { Lead } from '@/api/types';
import { useProjectContext } from '@/project/ProjectContext';
import { LeadFilters } from '@/features/leads/LeadFilters';
import { LeadDetailPanel } from '@/features/leads/LeadDetailPanel';
import { CsvImportExport } from '@/features/leads/CsvImportExport';
import { CreateLeadModal } from '@/features/leads/CreateLeadModal';
import { toQueryParams, type LeadFilterState } from '@/features/leads/leadQuery';
import { StatusBadge } from '@/components/StatusBadge/StatusBadge';
import { Button } from '@/components/ui/Button';
import { ErrorState, LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { ProjectPickerEmpty } from '@/features/projects/ProjectPickerEmpty';
import { ProjectWorkspaceHeader } from '@/features/projects/ProjectWorkspaceHeader';
import { LeadWorkspaceLayout } from '@/features/leads/LeadWorkspaceLayout';
import styles from '@/features/leads/Leads.module.css';

const defaultFilters: LeadFilterState = {
  q: '',
  status: '',
  source: '',
  tag: '',
  page: 1,
  pageSize: 25,
};

export function LeadsPage() {
  const qc = useQueryClient();
  const { selectedProjectId, selectedProject, setSelectedProjectId } = useProjectContext();
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const queryString = useMemo(
    () => toQueryParams(filters, selectedProjectId).toString(),
    [filters, selectedProjectId],
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leads', queryString],
    enabled: Boolean(selectedProjectId),
    queryFn: () =>
      apiFetch<{ leads: Lead[]; total: number; page: number; pageSize: number }>(
        `/api/leads?${queryString}`,
      ),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const bulkScanEmail = useMutation({
    mutationFn: (body: { lead_ids: string[]; replace_existing?: boolean }) =>
      apiFetch('/api/leads/bulk/scan-website-email', {
        method: 'POST',
        body: JSON.stringify({ project_id: selectedProjectId, ...body }),
      }),
    onSuccess: () => {
      setChecked(new Set());
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const bulkPatch = useMutation({
    mutationFn: (body: { lead_ids: string[]; status?: string; do_not_contact?: boolean }) =>
      apiFetch('/api/leads/bulk', {
        method: 'POST',
        body: JSON.stringify({ project_id: selectedProjectId, ...body }),
      }),
    onSuccess: () => {
      setChecked(new Set());
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  function toggleAll(onPage: Lead[]) {
    const allSelected = onPage.every((l) => checked.has(l.id));
    const next = new Set(checked);
    if (allSelected) onPage.forEach((l) => next.delete(l.id));
    else onPage.forEach((l) => next.add(l.id));
    setChecked(next);
  }

  return (
    <ProjectGate pageTitle="Leads">
      {!selectedProjectId ? (
        <ProjectPickerEmpty onSelect={setSelectedProjectId} />
      ) : (
        <div className={styles.page}>
          <ProjectWorkspaceHeader
            title="Leads"
            subtitle="Contacten, bulk-import en outbound-ready lijsten per project."
            projectName={selectedProject?.name ?? 'Project'}
            onSwitchProject={() => setSelectedProjectId(null)}
          />

          <div className={styles.toolbar}>
            <LeadFilters filters={filters} onChange={setFilters} />
            <Button type="button" onClick={() => setCreateOpen(true)}>
              Nieuwe lead
            </Button>
          </div>

          <CsvImportExport />

          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState message={(error as Error).message} />
          ) : !data?.leads.length ? (
            <StateMessage title="Geen leads">
              Pas filters aan, maak handmatig een lead aan of importeer bulk via Excel/CSV.
            </StateMessage>
          ) : (
            <LeadWorkspaceLayout
              detailOpen={Boolean(selectedId)}
              onDetailDismiss={() => setSelectedId(null)}
              main={
              <div>
                {checked.size ? (
                  <div className={styles.bulkBar}>
                    <span>{checked.size} geselecteerd</span>
                    <select
                      className={styles.filterInput}
                      value={bulkStatus}
                      onChange={(e) => setBulkStatus(e.target.value)}
                    >
                      <option value="">Status…</option>
                      {LEAD_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {LEAD_STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="secondary"
                      disabled={!bulkStatus || bulkPatch.isPending}
                      onClick={() =>
                        bulkPatch.mutate({ lead_ids: [...checked], status: bulkStatus })
                      }
                    >
                      Status toepassen
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={bulkPatch.isPending}
                      onClick={() =>
                        bulkPatch.mutate({ lead_ids: [...checked], do_not_contact: true })
                      }
                    >
                      DNC
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={bulkScanEmail.isPending}
                      onClick={() => bulkScanEmail.mutate({ lead_ids: [...checked] })}
                    >
                      Website scannen (e-mail)
                    </Button>
                    <Button variant="ghost" onClick={() => setChecked(new Set())}>
                      Deselecteer
                    </Button>
                  </div>
                ) : null}
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            aria-label="Alles op pagina"
                            checked={
                              !!data.leads.length && data.leads.every((l) => checked.has(l.id))
                            }
                            onChange={() => toggleAll(data.leads)}
                          />
                        </th>
                        <th>Contact</th>
                        <th>Bedrijf</th>
                        <th>Status</th>
                        <th>Bron</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.leads.map((lead) => (
                        <tr
                          key={lead.id}
                          className={lead.id === selectedId ? styles.rowActive : undefined}
                          onClick={() => setSelectedId(lead.id)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={checked.has(lead.id)}
                              onChange={() => {
                                const next = new Set(checked);
                                if (next.has(lead.id)) next.delete(lead.id);
                                else next.add(lead.id);
                                setChecked(next);
                              }}
                            />
                          </td>
                          <td>{lead.contact_name ?? lead.email ?? '—'}</td>
                          <td>{lead.company ?? '—'}</td>
                          <td>
                            <StatusBadge status={lead.status} />
                          </td>
                          <td>{lead.source_code ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className={styles.pagination}>
                  <Button
                    variant="secondary"
                    disabled={filters.page <= 1}
                    onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
                  >
                    Vorige
                  </Button>
                  <span>
                    Pagina {filters.page} / {totalPages} ({data.total} totaal)
                  </span>
                  <Button
                    variant="secondary"
                    disabled={filters.page >= totalPages}
                    onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                  >
                    Volgende
                  </Button>
                </div>
              </div>
              }
              detail={
                selectedId ? (
                  <LeadDetailPanel leadId={selectedId} onClose={() => setSelectedId(null)} />
                ) : null
              }
            />
          )}

          {createOpen && selectedProjectId ? (
            <CreateLeadModal
              projectId={selectedProjectId}
              onClose={() => setCreateOpen(false)}
              onCreated={(lead) => setSelectedId(lead.id)}
            />
          ) : null}
        </div>
      )}
    </ProjectGate>
  );
}
